import { v4 as uuidv4 } from 'uuid';
import { useRef, useState, useEffect } from 'react';

import { api } from 'api';
import { useAppDispatch } from 'store';
import { useAlert, handleMutation } from 'hooks';
import { cartApi, tagTypes } from 'store/api/cart';
import { CloverPayment, PaymentIntent, PaymentIntentStatus } from 'types';
import {
  getExternalPaymentId,
  saveExternalPaymentId,
  clearExternalPaymentId,
} from 'utils/storage';
import {
  useResetDeviceMutation,
  useCancelPaymentMutation,
  useInitiatePaymentMutation,
} from 'store/api/clover';

import { PaymentStatus } from '../types';

import { useProcessingClover } from './use-processing-payment';

interface UseCreditPayment {
  eventId: number;
  deviceId: number;
  checkoutId?: number;
  creditAmounts: number;
  processingFee: number;
  onSuccessPayment?: (payment: CloverPayment) => void;
}

const POLLING_INTERVAL = 5000;
const MAX_POLLING_ATTEMPTS = 60;
const CANCEL_ENABLE_THRESHOLD = 5;

export const useCreditPayment = ({
  eventId,
  deviceId,
  checkoutId,
  creditAmounts,
  processingFee,
  onSuccessPayment,
}: UseCreditPayment) => {
  const dispatch = useAppDispatch();
  const { showErrorAlert, showSuccessAlert } = useAlert();

  const pollingAttemptsRef = useRef(0);
  const animationFinishedRef = useRef(false);
  const shouldStopPollingRef = useRef(false);
  const isProcessingInCloverRef = useRef(false);
  const pendingStatusRef = useRef<PaymentStatus | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const [isCancelDisabled, setCancelDisabled] = useState(true);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('idle');
  const [paymentIntentId, setPaymentIntentId] = useState<number | null>(null);
  const [paymentPendingAnimation, setPaymentPendingAnimation] = useState(false);

  const [resetDevice, resetMutation] = useResetDeviceMutation();
  const [cancelPayment, cancelPaymentMutation] = useCancelPaymentMutation();
  const [initiatePayment, initiatePaymentMutation] =
    useInitiatePaymentMutation();

  handleMutation(resetMutation);
  handleMutation(cancelPaymentMutation);
  handleMutation(initiatePaymentMutation);

  useProcessingClover({ setCancelDisabled, isProcessingInCloverRef });

  const completePaymentStatus = (status: PaymentStatus) => {
    if (animationFinishedRef.current) {
      setPaymentStatus(status);
    } else {
      pendingStatusRef.current = status;
    }
  };

  const stopPolling = () => {
    shouldStopPollingRef.current = true;

    if (pollingIntervalRef.current) {
      clearTimeout(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    pollingAttemptsRef.current = 0;
  };

  const handlePollingAttempt = () => {
    pollingAttemptsRef.current += 1;

    if (
      pollingAttemptsRef.current >= CANCEL_ENABLE_THRESHOLD &&
      isCancelDisabled &&
      !isProcessingInCloverRef.current
    ) {
      setCancelDisabled(false);
    }
  };

  const handlePaymentStatusResponse = (statusData: PaymentIntent): boolean => {
    const {
      status,
      canRetry,
      errorAction,
      errorMessage,
      cloverPayment,
      shouldResetExternalId,
    } = statusData;

    if (canRetry) {
      console.log('[PAYMENT] Network error on backend, awaiting manual retry');
      stopPolling();
      completePaymentStatus('failure');
      setPaymentPendingAnimation(true);
      showErrorAlert('Network error. Please try again');
      return true;
    }

    if (status === PaymentIntentStatus.Success) {
      console.log('[PAYMENT] Payment successful');
      stopPolling();
      completePaymentStatus('success');
      setPaymentPendingAnimation(true);
      dispatch(cartApi.util.invalidateTags(tagTypes));

      if (cloverPayment) {
        onSuccessPayment?.(cloverPayment);
      }

      clearExternalPaymentId(String(eventId));
      return true;
    }

    if (status === PaymentIntentStatus.Failed) {
      console.log('[PAYMENT] Payment failed:', errorMessage);
      stopPolling();
      completePaymentStatus('failure');
      setPaymentPendingAnimation(true);

      if (cloverPayment) {
        dispatch(cartApi.util.invalidateTags(tagTypes));
        onSuccessPayment?.(cloverPayment);
        clearExternalPaymentId(String(eventId));
        showSuccessAlert('Payment was completed, return result to cart');
      }

      if (shouldResetExternalId) {
        clearExternalPaymentId(String(eventId));
      }

      if (errorMessage) {
        showErrorAlert(errorMessage, { description: errorAction });
      }

      return true;
    }

    console.log('[PAYMENT] Payment still pending');
    return false;
  };

  const checkPaymentStatus = async (intentId: number) => {
    try {
      console.log(
        '[POLLING] Checking payment status, attempt:',
        pollingAttemptsRef.current + 1
      );

      const { data } = await api.payment.getPaymentStatus({ intentId });

      console.log('[POLLING] payment status response', data);

      const isComplete = handlePaymentStatusResponse(data);

      if (isComplete) {
        return true;
      }

      handlePollingAttempt();

      if (pollingAttemptsRef.current >= MAX_POLLING_ATTEMPTS) {
        console.error('[POLLING] Payment timeout - max attempts reached');
        stopPolling();
        completePaymentStatus('failure');
        setPaymentPendingAnimation(true);
        showErrorAlert('Payment processing timeout. Please try again.');
        return true;
      }

      return false;
    } catch (error) {
      console.error('[POLLING] Error checking payment status:', error);

      handlePollingAttempt();

      if (pollingAttemptsRef.current >= MAX_POLLING_ATTEMPTS) {
        stopPolling();
        completePaymentStatus('failure');
        setPaymentPendingAnimation(true);
        showErrorAlert('Network error while checking payment status');
        return true;
      }

      return false;
    }
  };

  const startPolling = async (intentId: number) => {
    console.log('[POLLING] Starting polling for intentId:', intentId);
    pollingAttemptsRef.current = 0;
    shouldStopPollingRef.current = false;

    const poll = async () => {
      if (shouldStopPollingRef.current) {
        console.log('[POLLING] Polling cancelled by flag');
        return;
      }

      const shouldStop = await checkPaymentStatus(intentId);

      if (shouldStop) {
        console.log('[POLLING] Polling stopped');
        return;
      }

      if (shouldStopPollingRef.current) {
        console.log('[POLLING] Polling cancelled before scheduling next check');
        return;
      }

      console.log('[POLLING] Scheduling next check in', POLLING_INTERVAL, 'ms');

      pollingIntervalRef.current = setTimeout(() => {
        poll();
      }, POLLING_INTERVAL);
    };

    await poll();
  };

  const handleCreditPayment = async () => {
    if (!creditAmounts) {
      console.warn('[PAYMENT] No credit amounts provided');
      return;
    }

    console.log('[PAYMENT] Starting credit payment');

    animationFinishedRef.current = false;
    pendingStatusRef.current = null;
    shouldStopPollingRef.current = false;

    setCancelDisabled(true);
    setPaymentStatus('idle');
    setShowPaymentDialog(true);
    setPaymentPendingAnimation(false);

    try {
      let externalPaymentId = getExternalPaymentId(String(eventId));
      if (!externalPaymentId) {
        externalPaymentId = uuidv4().replace(/-/g, '').slice(0, 32);
        saveExternalPaymentId(String(eventId), externalPaymentId);
      }

      console.log(
        '[PAYMENT] Initiating payment with externalPaymentId:',
        externalPaymentId
      );

      const initiateResponse = await initiatePayment({
        eventId,
        deviceId,
        checkoutId,
        processingFee,
        externalPaymentId,
        amount: creditAmounts,
      }).unwrap();

      const {
        status,
        cloverPayment,
        paymentIntentId: intentId,
      } = initiateResponse;

      console.log('[PAYMENT] Initiate response - status:', status);

      setPaymentIntentId(intentId);

      const isComplete = handlePaymentStatusResponse(initiateResponse);

      if (isComplete) {
        return cloverPayment;
      }

      if (status === PaymentIntentStatus.Pending) {
        console.log('[PAYMENT] Payment pending, starting polling');
        startPolling(intentId);
      }
    } catch (error: any) {
      console.error('[PAYMENT] Payment initiation error:', error);

      stopPolling();
      completePaymentStatus('failure');
      setPaymentPendingAnimation(true);

      const shouldResetExternalId =
        error?.data?.shouldResetExternalId || error?.shouldResetExternalId;

      if (shouldResetExternalId) {
        clearExternalPaymentId(String(eventId));
      }
      const errorAction = error?.data?.error?.action ?? undefined;
      const errorMessage =
        error?.data?.message || error?.message || 'Payment initiation failed';

      showErrorAlert(errorMessage, { description: errorAction });
    }
  };

  const onCancelPayment = async () => {
    console.log('[PAYMENT] Cancelling payment');
    stopPolling();
    setPaymentPendingAnimation(true);

    if (!paymentIntentId) {
      await resetDevice({ deviceId });
      setPaymentPendingAnimation(false);
      clearExternalPaymentId(String(eventId));
      completePaymentStatus('failure');
      setShowPaymentDialog(false);
      return;
    }

    if (paymentIntentId) {
      try {
        const response = await cancelPayment(paymentIntentId).unwrap();
        console.log('[PAYMENT]: cancel payment result', response);

        console.log('[PAYMENT]: Resetting device');
        await resetDevice({ deviceId });

        await new Promise((resolve) => setTimeout(resolve, 3000));

        const { data } = await api.payment.getPaymentStatus({
          intentId: paymentIntentId,
        });

        console.log('[PAYMENT]: get payment status result', data);

        clearExternalPaymentId(String(eventId));

        if (data.status === PaymentIntentStatus.Success && data.cloverPayment) {
          showSuccessAlert('Payment was completed, return result to cart');
          completePaymentStatus('success');
          onSuccessPayment?.(data.cloverPayment);
          setShowPaymentDialog(false);
          return;
        }
      } catch (error) {
        console.error('[PAYMENT] Error during cancellation:', error);
        await resetDevice({ deviceId });
      } finally {
        setPaymentPendingAnimation(false);
      }
    }

    clearExternalPaymentId(String(eventId));
    setPaymentIntentId(null);
    completePaymentStatus('failure');
    setShowPaymentDialog(false);
  };

  const handleAnimationComplete = () => {
    animationFinishedRef.current = true;
    if (pendingStatusRef.current) {
      setPaymentStatus(pendingStatusRef.current);
      pendingStatusRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      console.log('[PAYMENT] Component unmounting, cleaning up');
      stopPolling();
    };
  }, []);

  return {
    paymentStatus,
    paymentIntentId,
    onCancelPayment,
    isCancelDisabled,
    showPaymentDialog,
    handleCreditPayment,
    setShowPaymentDialog,
    handleAnimationComplete,
    paymentPendingAnimation,
  };
};
